import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Portal } from 'react-portal'

import { LANGUAGES } from '../../constants/languages'
import useDestroy from '../../hooks/useDestroy'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import type { Language } from '../../types/common'
import classNames from '../../utils/classNames'

interface GlobeLanguageSelectorProps {
  hideDivider?: boolean
}

const GlobeLanguageSelector = ({ hideDivider }: GlobeLanguageSelectorProps) => {
  const { i18n } = useTranslation()
  const { language, changeLanguage } = i18n
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const { isDestroyed } = useDestroy({ isVisible: isOpen })

  useOnClickOutside(ref, () => {
    setTimeout(() => setIsOpen(false), 200)
  })

  const setLanguage = async (newLanguage: Language) => {
    await changeLanguage(newLanguage)
    document.documentElement.setAttribute('lang', newLanguage)

    if (
      window.location.pathname === '/casino/favoritos' ||
      window.location.pathname === '/casino/favorites'
    ) {
      window.location.replace(
        newLanguage === 'en' ? '/casino/favorites' : '/casino/favoritos',
      )
    } else {
      window.location.reload()
    }
  }

  return (
    <div className="flx-ctr">
      {!hideDivider && (
        <div className="h-5 w-px bg-txt ml-2.5 xl:h-[25px] xl:w-0.5 xl:ml-[15px]" />
      )}
      <button
        className="flex cursor-pointer relative uppercase text-xs font-Syncopate-Bold text-txt -mr-2.5 p-2.5 xl:text-sm xl:mr-[-15px] xl:p-[15px]"
        ref={ref}
        onClick={() => setIsOpen(prev => !prev)}>
        {language}
      </button>
      {!isDestroyed && (
        <Portal>
          <div
            className={classNames(
              'bg-secondary fixed w-[150px] transition-all duration-[0.2s] ease-[ease-in-out] animate-fadeIn z-[100]',
              'rounded-[7px] right-5 top-[43px] xl:w-[200px] xl:right-[90px] 2xl:right-[150px] 3xl:right-[250px]',
              isOpen ? 'opacity-100' : 'opacity-0',
            )}>
            {LANGUAGES.map(lang => (
              <button
                key={lang.key}
                onClick={() => setLanguage(lang.key)}
                className={classNames(
                  'text-base w-full text-left cursor-pointer leading-[18px] flex items-center px-5 py-2.5',
                  language === lang.key
                    ? 'text-[#df0033]'
                    : 'text-[#888] dark:text-white',
                )}>
                <img
                  className="w-[17px] mr-2 -mt-px"
                  src={lang.image}
                  alt={lang.name}
                />
                {lang.name}
              </button>
            ))}
          </div>
        </Portal>
      )}
    </div>
  )
}

export default GlobeLanguageSelector
